'use client';

/**
 * Settings › Notifications: every notification (same list as the side panel),
 * and a switch for each kind. Switching a kind off stops new ones of that kind;
 * the admins' Site list can be left out of the bell's count the same way.
 */

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useNotifyCounts, fetchPrefs, savePrefs, refreshCounts, PREF_KINDS, type Notice } from '@/lib/notifications';
import { NoticeList } from './NoticeList';

export default function NotificationSettings({ onTab, onClose }: { onTab: (tab: string) => void; onClose: () => void }) {
  const router = useRouter();
  const c = useNotifyCounts(true);
  const [off, setOff] = useState<string[] | null>(null);
  const [admin, setAdmin] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { fetchPrefs().then((p) => { setOff(p.off); setAdmin(p.admin); }).catch(() => setOff([])); }, []);

  const open = (n: Notice) => {
    if (n.href.startsWith('#')) { onTab(n.href.slice(1)); return; }
    if (n.href.startsWith('/')) { onClose(); router.push(n.href); }
  };
  const toggle = async (id: string) => {
    if (!off) return;
    const next = off.includes(id) ? off.filter((x) => x !== id) : [...off, id];
    setOff(next); setMsg('');
    try { await savePrefs(next); setMsg('Saved.'); void refreshCounts(); } catch (e) { setMsg((e as Error).message); setOff(off); }
  };

  return (
    <div className="grid gap-6">
      <section className="flex h-[min(520px,60dvh)] flex-col overflow-hidden rounded-2xl border border-ink-200 dark:border-white/10">
        <NoticeList admin={c.admin || admin} mineCount={c.mine} siteCount={c.site} onOpen={open} />
      </section>

      <section>
        <h3 className="text-[14px] font-bold text-ink-900 dark:text-ink-50">What to tell me about</h3>
        <ul className="mt-2 divide-y divide-ink-100 rounded-2xl border border-ink-200 dark:divide-white/[0.06] dark:border-white/10">
          {PREF_KINDS.filter((k) => !k.admin || c.admin || admin).map((k) => {
            const on = !!off && !off.includes(k.id);
            return (
              <li key={k.id} className="flex items-center gap-3 px-4 py-3">
                <span className="min-w-0 flex-1">
                  <span className="block text-[13.5px] font-semibold text-ink-900 dark:text-ink-50">{k.label}</span>
                  <span className="block text-[12px] text-ink-500 dark:text-ink-400">{k.hint}</span>
                </span>
                <button type="button" role="switch" aria-checked={on} aria-label={k.label} disabled={!off} onClick={() => toggle(k.id)}
                        className={'relative h-6 w-11 shrink-0 rounded-full transition ' + (on ? 'bg-accent-600 dark:bg-accent-400' : 'bg-ink-300 dark:bg-white/15')}>
                  <span className={'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ' + (on ? 'left-[22px]' : 'left-0.5')} />
                </button>
              </li>
            );
          })}
        </ul>
        {msg && <p role="status" className="mt-2 text-[12.5px] text-ink-500">{msg}</p>}
      </section>
    </div>
  );
}
